import {Stack, Typography, useTheme} from '@mui/material';
import React, {Fragment} from 'react';

import {DataInfo} from 'presentation/components';
import {usePersonalData} from 'presentation/hooks';
import {useMonitoringTime} from 'presentation/pages/private/hooks/useMonitoringTime';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {translations} from 'presentation/translations-new';
import {formatMasks, formatText, getUserInfoFromToken} from 'presentation/utils';
import {BookingTypeEnum} from '../../constants';

import {AppointmenInstructionsAlertInfo} from 'presentation/modules/booking/shared/ui/components/AppointmenInstructionsAlertInfo';
import {ConfirmationCard} from '../ConfirmationCard';
import {AppointmentInstructionsProps} from './types';

const {beneficiaryPlan, contactInfo} = translations['pt-br'].commons;
const {appointmentInstructions} =
	translations['pt-br'].privateFlow.myAppointments.myAppointmentDetails;
const {
	cardData: {cardCode},
} = translations['pt-br'].privateFlow.healthCard;
const {beneficiaryData} = translations['pt-br'].privateFlow.bookingFlow;

export function AppointmentInstructions({
	appointment,
	hideAction,
	isHistoryDetails,
}: AppointmentInstructionsProps) {
	const {colors} = useTheme();
	const isTelehealth = appointment.bookingType === BookingTypeEnum.TELEHEALTH;
	const {goToTelehealthAvailability} = useMonitoringTime(
		appointment?.dateUtc ?? appointment.date,
	);

	const showEnterRoom = isTelehealth && goToTelehealthAvailability;

	const {
		beneficiary: {nmPlano, nmSocialUsuarioC, nmUsuarioC},
	} = useAccountDataStore(state => state.accountData);
	const {currentEmail, currentPhone} = usePersonalData();

	const {cdUsuario} = getUserInfoFromToken();

	return (
		<Stack gap={2}>
			{isHistoryDetails ? null : (
				<Typography variant="subtitle2" color={colors.light.text.secondary}>
					{beneficiaryData}
				</Typography>
			)}
			<ConfirmationCard.Root>
				<ConfirmationCard.Header subheader={beneficiaryPlan} />
				<ConfirmationCard.Content
					iconName="account-circle-filled"
					primaryText={formatText(nmSocialUsuarioC || nmUsuarioC)}>
					<DataInfo
						primaryText={nmPlano}
						iconName="hapvida-logo"
						secondaryText={`${cardCode}: ${cdUsuario}`}
						listItemProps={{sx: {p: 0}}}
						secondaryTextColor="text.secondary"
					/>
				</ConfirmationCard.Content>
			</ConfirmationCard.Root>
			{isHistoryDetails ? null : (
				<Fragment>
					<ConfirmationCard.Root>
						<ConfirmationCard.Header subheader={contactInfo} />
						<Stack>
							<DataInfo
								primaryText={formatMasks('phone', currentPhone)}
								iconName="phone-android-filled"
								listItemProps={{sx: {p: 0}}}
								removePaddingBottom
							/>
							<DataInfo
								primaryText={currentEmail}
								iconName="mail-filled"
								listItemProps={{sx: {p: 0}}}
							/>
						</Stack>
					</ConfirmationCard.Root>
					<Typography variant="subtitle2" color={colors.light.text.secondary}>
						{appointmentInstructions.title}
					</Typography>
					<AppointmenInstructionsAlertInfo
						showEnterRoom={showEnterRoom}
						hideAction={hideAction}
						bookingType={appointment.bookingType}
						specialtyCode={appointment.specialtyCode}
						specialtyName={appointment.specialtyName}
					/>
				</Fragment>
			)}
		</Stack>
	);
}
